import React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from 'context/providers/ToastProvider';

/**
 * Hook personalizado para manejar las operaciones del cliente
 * @param {Object} params - Parámetros del hook
 * @param {Object} params.customerdetails - Detalles del cliente
 * @param {Function} params.editarInfoCliente - Función para editar información del cliente
 * @param {Function} params.eliminarCliente - Función para eliminar cliente
 * @param {Function} params.registrarSoporteTecnico - Función para registrar soporte técnico
 * @param {Function} params.combinarClientes - Función para combinar clientes
 * @param {Function} params.obtenerComboBoxSoporteTecnico - Función para obtener combobox de soporte
 * @param {Function} params.abrirModalDatosFiscalesIncompletos - Función para abrir modal de datos fiscales incompletos
 * @param {Function} params.abrirModalNuevoSoporte - Función para abrir modal de nuevo soporte
 * @param {Function} params.cerrarModalNuevoSoporte - Función para cerrar modal de nuevo soporte
 * @param {Function} params.cerrarModalEliminar - Función para cerrar modal de eliminar
 * @param {Function} params.cerrarModalCombinarClientes - Función para cerrar modal de combinar clientes
 * @param {Function} params.resetFormSoporte - Función para resetear formulario de soporte
 */
const useClienteOperaciones = ({
    customerdetails,
    editarInfoCliente,
    eliminarCliente,
    registrarSoporteTecnico,
    combinarClientes,
    obtenerComboBoxSoporteTecnico,
    abrirModalDatosFiscalesIncompletos,
    abrirModalNuevoSoporte,
    cerrarModalNuevoSoporte,
    cerrarModalEliminar,
    cerrarModalCombinarClientes,
    resetFormSoporte
}) => {
    const navigate = useNavigate();
    const { showErrorToast, showSuccessToast } = useToast();
    const [idClienteAkasiaACombinar, setIdClienteAkasiaACombinar] = useState(0);
    const [habilitarBtnCombinar, setHabilitarBtnCombinar] = useState(true);
    const [idLead, setIdLead] = useState(0);

    /**
     * Mensaje que describe los campos fiscales faltantes (se expone para usar en UI)
     */
    const mensajeDatosFiscales = React.useMemo(() => {
        const missing = [];
        if (!customerdetails) return 'Completa los datos fiscales del cliente';
        if (!customerdetails.vRfc) missing.push('RFC');
        if (!customerdetails.vRazonSocial && !(customerdetails.vNombre || customerdetails.vApellidos)) {
            missing.push('Razón social / Nombre');
        }
        // varios nombres posibles para codigo postal
        if (!customerdetails.vCp && !customerdetails.vCodigoPostal && !customerdetails.vCP) missing.push('Código postal');
        if (!customerdetails.idCatRegimenFiscal && !customerdetails.vCodigoRegimen) missing.push('Régimen fiscal');
        return missing.length ? `Faltan los siguientes datos: ${missing.join(', ')}` : 'Completa los datos fiscales del cliente';
    }, [customerdetails]);

    /**
     * Valida los datos fiscales del cliente
     * @param {number} idClienteAkasia - ID del cliente
     */
    const validarDatosFiscales = (idClienteAkasia) => {
        const camposRequeridos = [
            'vRfc',
            'vRazonSocial',
            'vCodigoRegimen',
            'vDireccionFiscal',
            'vCodigoUso'
        ];

        const datosCompletos = camposRequeridos.every(campo => {
            const valor = customerdetails && customerdetails[campo];
            return valor != null && valor !== "";
        });

        if (datosCompletos) {
            navigate('/ordenes/nueva/' + idClienteAkasia);
        } else {
            // asegúrate de que mensajeDatosFiscales esté calculado y disponible antes de abrir el modal
            abrirModalDatosFiscalesIncompletos();
        }
    };

    /**
     * Maneja la obtención del combobox de soporte técnico
     */
    const handleObtenerComboBoxSoporteTecnico = async () => {
        try {
            await obtenerComboBoxSoporteTecnico();
            abrirModalNuevoSoporte();
        } catch (error) {
            showErrorToast(error.message || 'Error al obtener información de soporte técnico');
        }
    };

    /**
     * Maneja el guardado de la información del cliente
     * @param {number} id - ID del cliente
     * @param {Object} data - Datos del formulario
     */
    const handleGuardarCliente = async (id, data) => {
        console.log('=== onSubmit llamado ===');
        console.log('Datos del formulario:', data);
        
        try {
            const result = await editarInfoCliente(id, JSON.stringify(data, null, 2));
            console.log('Resultado de editarInfoCliente:', result);
            
            if (result.success) {
                showSuccessToast(result.message);
            }
        } catch (error) {
            console.error('Error en onSubmit:', error);
            showErrorToast(error.message || 'Error al guardar la información del cliente');
        }
    };

    /**
     * Maneja el registro de soporte técnico
     * @param {Object} data - Datos del soporte técnico
     */
    const handleRegistrarSoporte = async (data) => {
        try {
            cerrarModalNuevoSoporte();
            const result = await registrarSoporteTecnico(JSON.stringify(data));
            
            if (result.success) {
                showSuccessToast(result.message);
                resetFormSoporte();
            }
        } catch (error) {
            showErrorToast(error.message || 'Error desconocido al registrar soporte técnico');
        }
    };

    /**
     * Maneja la eliminación del cliente
     */
    const handleEliminarCliente = async () => {
        try {
            const result = await eliminarCliente();
            
            if (result.success) {
                showSuccessToast(result.message);
                cerrarModalEliminar();
            }
        } catch (error) {
            showErrorToast(error.message || 'Error desconocido al eliminar el cliente');
            cerrarModalEliminar();
        }
    };

    /**
     * Maneja la combinación de clientes
     */
    const handleCombinarClientes = async () => {
        setHabilitarBtnCombinar(false);
        
        try {
            await combinarClientes(idClienteAkasiaACombinar);
            cerrarModalCombinarClientes();
        } catch (error) {
            showErrorToast(error.message || 'Error al combinar clientes');
        } finally {
            setHabilitarBtnCombinar(true);
        }
    };

    return {
        // Estados
        idClienteAkasiaACombinar,
        setIdClienteAkasiaACombinar,
        habilitarBtnCombinar,
        idLead,
        setIdLead,
        
        // Métodos
        validarDatosFiscales,
        handleObtenerComboBoxSoporteTecnico,
        handleGuardarCliente,
        handleRegistrarSoporte,
        handleEliminarCliente,
        handleCombinarClientes,

        // Datos calculados
        mensajeDatosFiscales
    };
};

export default useClienteOperaciones;
